/**
 * §5.8 (docs/KONZEPT-ANFRAGE-ZU-ANGEBOT.md) — „Nicht eingelesene Dokumente".
 *
 * DER BEFUND (Prod-Messung 08.08.2026, Neon `br-blue-firefly-asguu1zm`): **11** Importe bei
 * STORIA stehen auf `status = 'failed'`. Sichtbar war davon bisher: nichts. Die Import-Seite
 * kennt nur den laufenden Vorgang, und `GET /api/menu-imports` wurde vom Frontend nie aufgerufen.
 * Elf hochgeladene Dokumente sind spurlos verschwunden — der Betrieb glaubt, seine Karte sei
 * eingelesen, und merkt es erst, wenn im Angebot die halbe Getränkekarte fehlt.
 *
 * Genau das ist schlimmer als ein sichtbarer Fehler: Ein Ergebnis, dem man vertraut, ohne zu
 * wissen, was darin fehlt.
 *
 * WAS DIE ZEILE ZEIGT: Datum, Dateiname, was nicht gelesen werden konnte, und was zu tun ist.
 * Der Originaltext des Fehlers bleibt über „Technische Meldung" erreichbar — verschluckt wird er
 * nicht, aber er steht nicht im Weg.
 *
 * EHRLICH BEI DEN ALTFÄLLEN: Für die elf Bestandszeilen wurde nie ein Dateiname gespeichert
 * (`source_files` trug nur `mediaType` + `r2Key`). Die Zeile sagt dann „Dateiname nicht
 * gespeichert" — sie erfindet keinen. Ab jetzt Hochgeladenes trägt seinen Namen mit.
 */
import { useState } from "react";
import { Icon, Card } from "./components";

export interface NichtEingelesenesDokument {
  id: string;
  sourceKind: string;
  /** null = für diesen (älteren) Import wurde nie ein Name gespeichert. */
  dateiname: string | null;
  dateienAnzahl: number;
  /** Ein lesbarer Satz über das, was nicht gelesen werden konnte (Server, `lib/import-fehler.ts`). */
  fehlerKurz: string;
  /** Der gespeicherte Originaltext — bleibt abrufbar, wird nie ersetzt. */
  fehlerText: string | null;
  handlung: string;
  createdAt: string;
}

function datum(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function DokumentZeile({ dok }: { dok: NichtEingelesenesDokument }) {
  const [rohOffen, setRohOffen] = useState(false);
  return (
    <li className="space-y-1.5 px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
          {dok.dateiname ?? "Dateiname nicht gespeichert"}
        </span>
        <span className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
          {datum(dok.createdAt)} · {dok.sourceKind.toUpperCase()}
          {dok.dateienAnzahl > 1 ? ` · ${dok.dateienAnzahl} Dateien` : ""}
        </span>
      </div>
      <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{dok.fehlerKurz}</p>
      <p className="font-body-sm text-body-sm text-on-surface-variant/80 dark:text-secondary-fixed-dim/80">{dok.handlung}</p>
      {dok.fehlerText && (
        <>
          <button
            type="button"
            aria-expanded={rohOffen}
            className="font-body-sm text-body-sm text-primary underline-offset-2 hover:underline"
            onClick={() => setRohOffen((v) => !v)}
          >
            Technische Meldung {rohOffen ? "ausblenden" : "anzeigen"}
          </button>
          {rohOffen && (
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-surface-container-low px-2.5 py-2 text-beleg-mikro text-on-surface-variant dark:bg-surface-variant/10">
              {dok.fehlerText}
            </pre>
          )}
        </>
      )}
    </li>
  );
}

export function NichtEingeleseneDokumente({ dokumente }: { dokumente: NichtEingelesenesDokument[] }) {
  // Kein Fehlschlag = kein Kasten. Eine dauerhaft sichtbare Leerzeile „0 Dokumente" wäre Rauschen.
  if (dokumente.length === 0) return null;
  return (
    <Card as="section" className="p-0">
      <div className="flex items-center gap-2 border-b border-outline-variant/20 px-4 py-3 dark:border-outline/20">
        <Icon name="report" className="text-[18px] text-secondary dark:text-[#b8ae9e]" />
        <h3 className="font-body-sm text-body-sm font-semibold text-on-background dark:text-inverse-on-surface">
          Nicht eingelesene Dokumente ({dokumente.length})
        </h3>
      </div>
      <p className="px-4 pt-3 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
        Diese Uploads sind nie im Katalog angekommen.
      </p>
      <ul className="divide-y divide-outline-variant/20 dark:divide-outline/20">
        {dokumente.map((d) => (
          <DokumentZeile key={d.id} dok={d} />
        ))}
      </ul>
    </Card>
  );
}
